import { useEffect, useRef } from "react";

// How far from the bottom still counts as "reading the newest block".
const NEAR_BOTTOM_PX = 48;

/**
 * Keeps the newest block in view while a report is being written. It stops
 * once the reader scrolls up, and starts again when they come back to the
 * bottom. Attach `scrollRef` to the element that scrolls.
 */
export function useFollowWhileWriting(isWriting: boolean) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!isWriting || !scroller) return;

    const isNearBottom = () =>
      scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <=
      NEAR_BOTTOM_PX;

    // Measured at the start, so a rewrite does not pull a reader who is
    // reading the top of the old report down to its bottom.
    let isFollowing = isNearBottom();

    const onScroll = () => {
      isFollowing = isNearBottom();
    };

    // A jump, not an animation: a smooth scroll in flight would look like the
    // reader scrolling up and switch following off. The block's own slide-in
    // is the motion.
    const observer = new MutationObserver(() => {
      if (isFollowing) scroller.scrollTop = scroller.scrollHeight;
    });

    scroller.addEventListener("scroll", onScroll);
    observer.observe(scroller, { childList: true, subtree: true });

    return () => {
      scroller.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, [isWriting]);

  return scrollRef;
}
